import { useState, useEffect } from "react";
import api from "@/lib/api";
import OnboardingBanner from "@/components/OnboardingBanner";
import { useProperties, usePropertyFinancials, useRooms, useInvalidate } from "@/lib/queries";
import { formatINR, PROPERTY_TYPES, AMENITIES_LIST } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Plus, PencilSimple, Trash, Bed, Bathtub, MapPin, TrendUp, UploadSimple, Link, X, Spinner, Image, VideoCamera, LockSimple, BookOpen, WarningCircle, Buildings, ArrowUp, ArrowDown, DownloadSimple } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/App";

const PROPERTY_PLAN_LIMITS = { free: 1, basic: 1, starter: 3, pro: 10, agency: -1, admin: -1 };
const PLAN_DISPLAY_NAMES   = { free: "Trial", basic: "Basic", starter: "Starter", pro: "Pro", agency: "Agency" };

export default function Properties() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Host decides per team member whether money figures are visible (2026-09-07)
  const hideRevenue = !!(user?.is_staff && !user?.show_revenue);
  // Dual booking links are enabled per account by an admin. When on, the host gets a
  // second link that prices and invoices the stay without GST; everyone else sees one link.
  const noGstToken   = user?.no_gst_link_enabled ? (user?.no_gst_link_token || "") : "";
  const dualLinks    = !!noGstToken;
  const bookingLink  = (p, withGst = true) => {
    const base = p.unit_type === "hotel"
      ? `${window.location.origin}/book/hotel/${p.id || p.property_id}`
      : `${window.location.origin}/book/${p.id || p.property_id}`;
    return withGst ? base : `${base}?ng=${encodeURIComponent(noGstToken)}`;
  };
  const copyLink = (link, label) =>
    navigator.clipboard.writeText(link)
      .then(() => toast.success(`${label} copied!`))
      .catch(() => toast.error("Could not copy"));
  const { data: properties = [], isLoading: loading } = useProperties();
  const invalidate = useInvalidate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailProp, setDetailProp] = useState(null);
  const { data: financials } = usePropertyFinancials(detailProp?.id);
  const [editProp, setEditProp] = useState(null);
  const [form, setForm] = useState(defaultForm());
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [guidebookProp, setGuidebookProp] = useState(null);
  const [guidebookRoom, setGuidebookRoom] = useState(null);
  const [exportProp, setExportProp] = useState(null);
  const [roomDialogOpen, setRoomDialogOpen] = useState(false);
  const [editRoom, setEditRoom] = useState(null);
  const [roomForm, setRoomForm] = useState(defaultRoomForm());
  const [roomUploading, setRoomUploading] = useState(false);
  const { data: detailRooms = [], refetch: refetchRooms } = useRooms(
    detailProp?.unit_type === "hotel" ? detailProp?.id : null
  );

  const openAddRoom = () => { setEditRoom(null); setRoomForm(defaultRoomForm()); setRoomDialogOpen(true); };
  const openEditRoom = (r) => { setEditRoom(r); setRoomForm({ ...r }); setRoomDialogOpen(true); };

  const handleSaveRoom = async () => {
    if (!roomForm.name?.trim()) { toast.error("Room name is required"); return; }
    try {
      const payload = {
        ...roomForm,
        property_id: detailProp.id,
        nightly_rate: parseFloat(roomForm.nightly_rate) || 0,
        bedrooms:     parseInt(roomForm.bedrooms)     || 1,
        bathrooms:    parseInt(roomForm.bathrooms)    || 1,
        max_guests:   parseInt(roomForm.max_guests)   || 2,
      };
      if (editRoom) {
        await api.put(`/rooms/${editRoom.room_id}`, payload);
        toast.success("Room updated");
      } else {
        await api.post("/rooms", payload);
        toast.success("Room added");
      }
      setRoomDialogOpen(false);
      refetchRooms();
      invalidate.properties();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save room"); }
  };

  const handleDeleteRoom = async (roomId) => {
    if (!window.confirm("Delete this room?")) return;
    try {
      await api.delete(`/rooms/${roomId}`);
      toast.success("Room deleted");
      refetchRooms();
      invalidate.properties();
    } catch { toast.error("Failed to delete room"); }
  };

  const handleRoomReorder = async (rooms) => {
    try {
      await api.put("/rooms/reorder", { room_ids: rooms.map(r => r.room_id) });
      refetchRooms();
      invalidate.properties();
    } catch { toast.error("Failed to reorder rooms"); }
  };

  const handleRoomPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
    setRoomUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = data.url || `${api.defaults.baseURL}/files/${data.storage_path}`;
      setRoomForm(f => ({ ...f, photos: [...(f.photos || []), url] }));
      toast.success("Photo uploaded!");
    } catch { toast.error("Upload failed"); } finally { setRoomUploading(false); }
  };

  const [formErrors, setFormErrors] = useState({});
  const [customEventType, setCustomEventType] = useState("");
  const [customEventPrice, setCustomEventPrice] = useState("");

  const needsPanVerification = user?.plan === "free" && !user?.trial_pan_set;
  const openAdd = () => {
    if (needsPanVerification) { navigate("/billing"); return; }
    setEditProp(null); setForm(defaultForm()); setFormErrors({}); setDialogOpen(true);
  };
  const openEdit = (p) => {
    if (p.locked) { toast.error("Upgrade your plan to edit this property."); navigate("/billing"); return; }
    setEditProp(p); setForm({ ...p }); setFormErrors({}); setDialogOpen(true);
  };

  const handleSave = async () => {
    const errs = {};
    if (!form.name?.trim())          errs.name          = "Property name is required";
    if (!form.address?.trim())       errs.address       = "Address is required";
    if (!form.neighbourhood?.trim()) errs.neighbourhood = "Required — used for AI pricing & guidebook";
    if (!form.pincode?.trim())       errs.pincode       = "Pincode is required";
    if (!form.city?.trim())          errs.city          = "City is required";
    if (Object.keys(errs).length) { setFormErrors(errs); toast.error("Please fill in the required fields highlighted below"); return; }
    setFormErrors({});
    try {
      const payload = {
        ...form,
        bedrooms:       parseInt(form.bedrooms)       || 1,
        bathrooms:      parseInt(form.bathrooms)      || 1,
        max_guests:     parseInt(form.max_guests)     || 0,
        purchase_price: parseFloat(form.purchase_price) || 0,
        emi_amount:     parseFloat(form.emi_amount)     || 0,
      };

      // Detect if turnover buffer is increasing so we can show a grandfathering notice
      const _toMins = (t, dflt) => { const [h, m] = String(t || dflt).split(":"); return (parseInt(h)||0)*60+(parseInt(m)||0); };
      const _calcBuf = (p) => {
        const buf = p.buffer_days || 0; const minH = p.min_turnover_hours || 0;
        if (buf === 0 && minH > 0 && (_toMins(p.checkin_time,"14:00") - _toMins(p.checkout_time,"11:00")) < minH*60) return 1;
        return buf;
      };
      const bufferIncreased = editProp && _calcBuf(payload) > _calcBuf(editProp);

      if (editProp) {
        await api.put(`/properties/${editProp.id}`, payload);
        if (bufferIncreased) {
          const nextMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1);
          const monthLabel = nextMonth.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
          toast.info(
            `Buffer updated. Your existing bookings keep the old policy — no disruption. The ${_calcBuf(payload)}-day gap applies to new bookings from ${monthLabel} onwards.`,
            { duration: 10000 }
          );
        } else {
          toast.success("Property updated");
        }
      } else {
        await api.post("/properties", payload);
        toast.success("Property added");
      }
      setDialogOpen(false);
      invalidate.afterPropertyMutation();
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error(e.response.data?.detail || "Plan limit reached. Upgrade to continue.", { action: { label: "Upgrade", onClick: () => navigate("/billing") } });
      } else {
        toast.error(e.response?.data?.detail || e.message || "Failed to save property");
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this property and all related data?")) return;
    try {
      await api.delete(`/properties/${id}`);
      toast.success("Property deleted");
      setDetailProp(null);
      invalidate.afterPropertyMutation();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const openDetail = (p) => { setDetailProp(p); };

  const toggleAmenity = (a) => {
    setForm(f => ({
      ...f,
      amenities: f.amenities.includes(a) ? f.amenities.filter(x => x !== a) : [...f.amenities, a]
    }));
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("File too large (max 10MB)"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      const fileUrl = data.url || `${api.defaults.baseURL}/files/${data.storage_path}`;
      setForm(f => ({ ...f, photos: [...(f.photos || []), fileUrl] }));
      toast.success("Photo uploaded!");
    } catch (err) { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const removePhoto = (index) => {
    setForm(f => ({ ...f, photos: f.photos.filter((_, i) => i !== index) }));
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("video/")) { toast.error("Please select a video file (mp4, mov or webm)"); return; }
    if (file.size > 60 * 1024 * 1024) { toast.error("Video too large (max 60 MB). Trim it or lower the resolution."); return; }
    setUploadingVideo(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/upload-video", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(f => ({ ...f, videos: [...(f.videos || []), data.url] }));
      toast.success("Video uploaded! Save the property to publish it.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Video upload failed"); } finally { setUploadingVideo(false); e.target.value = ""; }
  };

  const removeVideo = async (index) => {
    const url = form.videos?.[index];
    setForm(f => ({ ...f, videos: (f.videos || []).filter((_, i) => i !== index) }));
    // Free the storage too; a pasted third-party URL is a no-op server-side.
    if (url) { try { await api.delete("/upload-video", { params: { url } }); } catch (_) {} }
  };

  const handleImportListing = async () => {
    if (!importUrl.trim()) { toast.error("Please enter an Airbnb URL"); return; }
    setImporting(true);
    try {
      const { data } = await api.post("/import-listing", { url: importUrl.trim() });
      if (data.error) { toast.error(data.error || "Import failed"); return; }
      setForm(f => ({
        ...f,
        name: data.name || f.name,
        city: data.city || f.city,
        state: data.state || f.state,
        property_type: data.property_type || f.property_type,
        bedrooms: data.bedrooms || f.bedrooms,
        bathrooms: data.bathrooms || f.bathrooms,
        max_guests: data.max_guests || f.max_guests,
        amenities: data.amenities?.length ? data.amenities : f.amenities,
        extra_amenities: data.extra_amenities?.length ? data.extra_amenities : (f.extra_amenities || []),
        photos: data.photos?.length ? data.photos : (f.photos || []),
      }));
      setImportDialogOpen(false);
      setDialogOpen(true);
      toast.success("Listing details imported successfully!");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Import failed. Check the URL and try again.";
      toast.error(msg);
    } finally { setImporting(false); }
  };

  const inputCls = "bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-800 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-400 mt-1 focus:border-amber-400 focus:ring-amber-400";
  const errCls   = "bg-slate-50 dark:bg-slate-700 border-red-400 dark:border-red-500 text-slate-800 dark:text-slate-100 placeholder:text-slate-400 mt-1 focus:border-red-400 focus:ring-red-400";
  const ic = (field) => formErrors[field] ? errCls : inputCls;
  const selectTriggerCls = "bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-800 dark:text-slate-100 mt-1";
  const btnCancel = "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600 font-medium rounded-xl px-5 py-2.5 text-sm transition-colors";
  const btnPrimary = "bg-amber-400 text-white hover:bg-amber-500 font-semibold rounded-xl px-5 py-2.5 text-sm transition-colors";
  const isDark = document.documentElement.classList.contains("dark");
  const tooltipStyle = { backgroundColor: isDark ? "#1e293b" : "#ffffff", border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`, borderRadius: "12px", fontSize: "11px", color: isDark ? "#e2e8f0" : "#334155", boxShadow: "0 4px 16px rgba(0,0,0,0.08)" };

  if (loading) return (
    <div className="animate-pulse space-y-4">
      <div className="h-8 w-48 bg-slate-200 rounded" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1,2,3].map(i => <div key={i} className="h-64 bg-slate-100 rounded-2xl" />)}
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="properties-page">
      <OnboardingBanner pageKey="properties" />
      {needsPanVerification && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-900/20 px-4 py-3.5">
          <WarningCircle size={18} weight="fill" className="text-amber-500 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">Identity verification required</p>
            <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">Enter your PAN in Billing to activate your free trial and add properties. This ensures one free trial per person.</p>
          </div>
          <button onClick={() => navigate("/billing")} className="shrink-0 text-xs font-semibold text-amber-800 dark:text-amber-300 underline underline-offset-2 hover:text-amber-900 dark:hover:text-amber-200 transition-colors">
            Verify now →
          </button>
        </div>
      )}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-manrope text-2xl md:text-3xl font-semibold text-slate-900 dark:text-amber-400 tracking-tight">Properties</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">{properties.length} properties</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { setImportUrl(""); setImportDialogOpen(true); }} className="bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600 border border-slate-200 dark:border-slate-600 font-medium rounded-xl px-3 py-2.5 text-sm flex items-center gap-2 transition-colors" data-testid="import-listing-button">
            <Link size={16} /> Import URL
          </button>
          {(() => {
            const planId = user?.plan || "free";
            const limit  = PROPERTY_PLAN_LIMITS[planId] ?? 1;
            const atLimit = limit !== -1 && properties.length >= limit;
            const planName = PLAN_DISPLAY_NAMES[planId] || planId;
            return (
              <div className="relative group">
                <button
                  onClick={atLimit ? undefined : openAdd}
                  className={atLimit
                    ? "cursor-not-allowed bg-slate-200 dark:bg-slate-700 text-slate-400 dark:text-slate-500 font-semibold rounded-xl px-4 py-2.5 text-sm flex items-center gap-2 select-none"
                    : "bg-amber-400 text-white hover:bg-amber-500 font-semibold rounded-xl px-4 py-2.5 text-sm flex items-center gap-2 transition-colors shadow-sm"
                  }
                  data-testid="add-property-button"
                >
                  {atLimit ? <LockSimple size={16} weight="duotone" /> : <Plus size={16} weight="bold" />}
                  Add Property
                </button>
                {atLimit && (
                  <div className="absolute right-0 top-full mt-2.5 w-72 z-50 opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none">
                    <div className="bg-slate-900 dark:bg-slate-800 text-white rounded-2xl px-4 py-3.5 shadow-2xl border border-slate-700">
                      <div className="flex items-center gap-2 mb-1.5">
                        <LockSimple size={14} weight="duotone" className="text-amber-400 shrink-0" />
                        <p className="font-semibold text-sm">Property limit reached</p>
                      </div>
                      <p className="text-slate-300 text-xs leading-relaxed">
                        Your <span className="text-white font-semibold">{planName}</span> plan includes{" "}
                        <span className="text-white font-semibold">{limit} {limit === 1 ? "property" : "properties"}</span>.
                        Upgrade your plan to manage more properties.
                      </p>
                      <button
                        onClick={() => navigate("/billing")}
                        className="pointer-events-auto mt-2.5 w-full py-1.5 bg-amber-400 hover:bg-amber-500 text-white text-xs font-semibold rounded-lg transition-colors"
                      >
                        Upgrade plan →
                      </button>
                    </div>
                    <div className="absolute -top-1.5 right-5 w-3 h-3 bg-slate-900 dark:bg-slate-800 rotate-45 border-l border-t border-slate-700" />
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Property Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {properties.map((p) => (
          <Card key={p.id} className={`bg-white dark:bg-slate-800 border-slate-200 rounded-2xl overflow-hidden shadow-sm ${p.locked ? "opacity-70" : "card-hover cursor-pointer"}`} onClick={() => !p.locked && openDetail(p)} data-testid={`property-card-${p.id}`}>
            <div className="h-44 bg-slate-100 dark:bg-slate-700 relative">
              {p.photos?.[0] ? (
                <img src={p.photos[0]} alt={p.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <MapPin size={40} className="text-slate-300" />
                </div>
              )}
              {p.locked ? (
                <div className="absolute inset-0 bg-slate-900/40 flex flex-col items-center justify-center gap-2">
                  <LockSimple size={28} className="text-white" weight="fill" />
                  <button onClick={e => { e.stopPropagation(); navigate("/billing"); }}
                    className="text-[11px] font-bold bg-amber-400 hover:bg-amber-500 text-white px-3 py-1.5 rounded-full transition">
                    Upgrade to unlock
                  </button>
                </div>
              ) : (
                <Badge variant="outline" className={`absolute top-3 right-3 backdrop-blur-sm ${
                  p.status === "active" ? "bg-emerald-50/90 text-emerald-600 border-emerald-200" :
                  p.status === "maintenance" ? "bg-amber-50/90 text-amber-600 border-amber-200" :
                  "bg-white/90 text-slate-300 border-slate-200"
                }`}>
                  {p.status}
                </Badge>
              )}
            </div>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-manrope text-base font-semibold text-slate-900 dark:text-white truncate">{p.name}</h3>
                {p.unit_type === "hotel" && (
                  <span className="shrink-0 flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-sky-50 dark:bg-sky-900/30 text-sky-600 dark:text-sky-300 border border-sky-100 dark:border-sky-700">
                    <Buildings size={10} /> Hotel
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">{p.city ? `${p.city}, ${p.state || ''}` : p.address}</p>
              <div className="flex items-center gap-4 mt-3 text-xs text-slate-500 dark:text-slate-400">
                {p.unit_type === "hotel" ? (
                  <span className="flex items-center gap-1"><Buildings size={14} /> {(p.rooms || []).length} room{(p.rooms || []).length !== 1 ? "s" : ""}</span>
                ) : (
                  <>
                    <span className="flex items-center gap-1"><Bed size={14} /> {p.bedrooms} BR</span>
                    <span className="flex items-center gap-1"><Bathtub size={14} /> {p.bathrooms} BA</span>
                  </>
                )}
                <span className="text-slate-400">{p.property_type}</span>
              </div>
              {p.listing_type && p.listing_type !== "short_term" && (
                <span className="inline-block mt-2 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-300 border border-violet-100 dark:border-violet-700">
                  {{ long_term: "Long-term", lease: "Lease", pg: "PG/Hostel", commercial: "Commercial", owner_occupied: "Owner Occupied" }[p.listing_type] || p.listing_type}
                </span>
              )}
              {p.ready_for_checkin && (
                <span className="inline-flex items-center gap-1 mt-2 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 border border-green-200 dark:border-green-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500" />✓ Ready for check-in
                </span>
              )}
              {p.dynamic_pricing_mode === "hostfolio" && (
                <span className="inline-flex items-center gap-1 mt-2 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 border border-violet-200 dark:border-violet-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-violet-400" />AI Pricing ON
                </span>
              )}
              {p.dynamic_pricing_mode === "aiosell" && (
                <span className="inline-flex items-center gap-1 mt-2 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                  {p.aiosell_pricing_status !== "active"
                    ? "Smart Pricing — setting up"
                    : user?.smart_pricing_subscription_id
                      ? "Smart Pricing ON"
                      : "Smart Pricing — needs auto-pay"}
                  {p.aiosell_pricing_status === "active" && user?.smart_pricing_subscription_id && p.dynamic_pricing_last_updated && (
                    <span className="font-normal text-amber-400/80 dark:text-amber-500/70 ml-0.5">
                      · {new Date(p.dynamic_pricing_last_updated).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                    </span>
                  )}
                </span>
              )}
              {!hideRevenue && p.emi_amount > 0 && (
                <p className="text-xs text-slate-400 mt-2">EMI: <span className="font-mono text-slate-700 dark:text-slate-200 font-medium">{formatINR(p.emi_amount)}/mo</span></p>
              )}
              {(p.listing_type === "short_term" || !p.listing_type) && (
                <div className="mt-3 space-y-2">
                  {dualLinks && (
                    <div className="flex gap-2">
                      <button
                        onClick={e => { e.stopPropagation(); copyLink(bookingLink(p, true), "Booking link with GST"); }}
                        title="Guest is charged GST and gets a tax invoice"
                        className="flex-1 text-[11px] font-semibold py-1.5 rounded-xl bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 transition flex items-center justify-center gap-1.5">
                        🔗 Link (with GST)
                      </button>
                      <button
                        onClick={e => { e.stopPropagation(); copyLink(bookingLink(p, false), "Booking link without GST"); }}
                        title="No GST is charged and the guest gets a plain invoice, not a tax invoice"
                        className="flex-1 text-[11px] font-semibold py-1.5 rounded-xl bg-slate-50 hover:bg-slate-100 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-200 border border-slate-200 dark:border-slate-600 transition flex items-center justify-center gap-1.5">
                        🔗 Link (no GST)
                      </button>
                    </div>
                  )}
                  <div className="flex gap-2">
                    {!dualLinks && (
                      <button
                        onClick={e => { e.stopPropagation(); copyLink(bookingLink(p, true), "Booking link"); }}
                        className="flex-1 text-[11px] font-semibold py-1.5 rounded-xl bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 transition flex items-center justify-center gap-1.5">
                        🔗 Booking Link
                      </button>
                    )}
                    <button
                      onClick={e => { e.stopPropagation(); setGuidebookProp(p); }}
                      className="flex-1 text-[11px] font-semibold py-1.5 rounded-xl bg-slate-50 hover:bg-slate-100 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-200 border border-slate-200 dark:border-slate-600 transition flex items-center justify-center gap-1.5">
                      <BookOpen size={12} /> Guidebook
                    </button>
                  </div>
                </div>
              )}
              <button
                onClick={e => { e.stopPropagation(); setExportProp(p); }}
                title="Export listing details + photos to list on Vrbo, Booking.com and other OTAs"
                className="w-full mt-2 text-[11px] font-semibold py-1.5 rounded-xl bg-teal-50 hover:bg-teal-100 dark:bg-teal-900/20 dark:hover:bg-teal-900/30 text-teal-700 dark:text-teal-300 border border-teal-200 dark:border-teal-800/40 transition flex items-center justify-center gap-1.5">
                <DownloadSimple size={12} /> Export for OTAs
              </button>
            </CardContent>
          </Card>
        ))}
        {properties.length === 0 && (
          <div className="col-span-full text-center py-16">
            <MapPin size={48} className="text-slate-300 mx-auto mb-4" />
            <p className="text-slate-400 text-sm">No properties yet. Add your first property to get started!</p>
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 max-w-lg max-h-[85vh] overflow-y-auto shadow-xl">
          <DialogHeader>
            <DialogTitle className="font-manrope text-slate-900 dark:text-white">{editProp ? "Edit Property" : "Add Property"}</DialogTitle>
            <DialogDescription className="text-slate-500 dark:text-slate-400">Enter your property details below.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Property Name <span className="text-red-500">*</span></Label>
                <Input value={form.name} onChange={e => { setForm(f => ({...f, name: e.target.value})); setFormErrors(fe => ({...fe, name: ""})); }} className={ic("name")} placeholder="Ocean View Villa" data-testid="property-name-input" />
                {formErrors.name && <p className="text-red-500 text-[10px] mt-1">{formErrors.name}</p>}
              </div>
              <div className="col-span-2">
                <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Unit Type</Label>
                <div className="flex gap-2 mt-1">
                  {[
                    { value: "full_unit", label: "Full Unit", desc: "Single listing — entire apartment, villa, etc." },
                    { value: "hotel",     label: "Hotel / Multi-Room", desc: "Multiple bookable rooms under one property" },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, unit_type: opt.value }))}
                      className={`flex-1 text-left px-3 py-2.5 rounded-xl border text-xs transition-all ${
                        form.unit_type === opt.value
                          ? "border-amber-400 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300"
                          : "border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 hover:border-slate-300"
                      }`}
                    >
                      <p className="font-semibold">{opt.label}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">{opt.desc}</p>
                    </button>
                  ))}
                </div>
                {form.unit_type === "hotel" && (
                  <div className="mt-2 flex items-start gap-2 rounded-xl bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-700 px-3 py-2">
                    <Buildings size={14} className="text-sky-500 shrink-0 mt-0.5" />
                    <p className="text-[11px] text-sky-700 dark:text-sky-300">Save this property first. Then click the property card to open it and add individual rooms from the <strong>Rooms</strong> tab.</p>
                  </div>
                )}
              </div>
              <div className="col-span-2">
                <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Address <span className="text-red-500">*</span></Label>
                <Input value={form.address} onChange={e => { setForm(f => ({...f, address: e.target.value})); setFormErrors(fe => ({...fe, address: ""})); }} className={ic("address")} placeholder="123 Beach Road" />
                {formErrors.address && <p className="text-red-500 text-[10px] mt-1">{formErrors.address}</p>}
              </div>
              <div className="col-span-2">
                <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Neighbourhood <span className="text-red-500">*</span></Label>
                <Input value={form.neighbourhood || ""} onChange={e => { setForm(f => ({...f, neighbourhood: e.target.value})); setFormErrors(fe => ({...fe, neighbourhood: ""})); }} className={ic("neighbourhood")} placeholder="e.g. Koramangala, Bandra West, Indiranagar" />
                {formErrors.neighbourhood
                  ? <p className="text-red-500 text-[10px] mt-1">{formErrors.neighbourhood}</p>
                  : <p className="text-[10px] text-slate-400 mt-1">Used for hyper-local AI pricing and guidebook suggestions</p>
                }
              </div>
              <div>
                <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">City <span className="text-red-500">*</span></Label>
                <Input value={form.city} onChange={e => { setForm(f => ({...f, city: e.target.value})); setFormErrors(fe => ({...fe, city: ""})); }} className={ic("city")} placeholder="Bengaluru" />
                {formErrors.city && <p className="text-red-500 text-[10px] mt-1">{formErrors.city}</p>}
              </div>
              <div>
                <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Pincode <span className="text-red-500">*</span></Label>
                <Input value={form.pincode} onChange={e => { setForm(f => ({...f, pincode: e.target.value})); setFormErrors(fe => ({...fe, pincode: ""})); }} className={ic("pincode")} placeholder="560034" />
                {formErrors.pincode && <p className="text-red-500 text-[10px] mt-1">{formErrors.pincode}</p>}
              </div>
              <div>
                <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Property Type <span className="text-red-500">*</span></Label>
                <Select value={form.property_type} onValueChange={v => setForm(f => ({...f, property_type: v}))}>
                  <SelectTrigger className={selectTriggerCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white dark:bg-slate-800 border-slate-200">
                    {PROPERTY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Listing Type <span className="text-red-500">*</span></Label>
                <Select value={form.listing_type || "short_term"} onValueChange={v => setForm(f => ({...f, listing_type: v}))}>
                  <SelectTrigger className={selectTriggerCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white dark:bg-slate-800 border-slate-200">
                    <SelectItem value="short_term">Short-term Rental (Airbnb / VRBO)</SelectItem>
                    <SelectItem value="long_term">Long-term Rental</SelectItem>
                    <SelectItem value="lease">Lease</SelectItem>
                    <SelectItem value="pg">PG / Hostel</SelectItem>
                    <SelectItem value="commercial">Commercial Lease</SelectItem>
                    <SelectItem value="owner_occupied">Owner Occupied</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Status <span className="text-red-500">*</span></Label>
                <Select value={form.status} onValueChange={v => setForm(f => ({...f, status: v}))}>
                  <SelectTrigger className={selectTriggerCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white dark:bg-slate-800 border-slate-200">
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="vacant">Vacant</SelectItem>
                    <SelectItem value="maintenance">Under Maintenance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Bedrooms <span className="text-red-500">*</span></Label>
                <Input type="number" min={1} value={form.bedrooms} onChange={e => setForm(f => ({...f, bedrooms: e.target.value === "" ? "" : parseInt(e.target.value) || 1}))} className={inputCls} placeholder="e.g. 2" />
              </div>
              <div>
                <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Bathrooms <span className="text-red-500">*</span></Label>
                <Input type="number" min={1} value={form.bathrooms} onChange={e => setForm(f => ({...f, bathrooms: e.target.value === "" ? "" : parseInt(e.target.value) || 1}))} className={inputCls} placeholder="e.g. 1" />
              </div>
              <div>
                <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Max Guests</Label>
                <Input type="number" min={1} max={50} value={form.max_guests} onChange={e => setForm(f => ({...f, max_guests: e.target.value === "" ? "" : parseInt(e.target.value) || ""}))} className={inputCls} placeholder="e.g. 6" />
              </div>
              <div>
                <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">
                  {form.listing_type === "long_term" || form.listing_type === "lease" || form.listing_type === "pg" || form.listing_type === "commercial"
                    ? "Monthly Rent / Lease Amount ₹" : "Nightly Rate ₹"}
                </Label>
                <Input type="number" value={form.nightly_rate || ""} onChange={e => setForm(f => ({...f, nightly_rate: parseFloat(e.target.value) || 0}))} className={inputCls}
                  placeholder={form.listing_type === "long_term" || form.listing_type === "lease" ? "25,000" : "3,500"} />
              </div>
              <div>
                <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Purchase Price ₹</Label>
                <Input type="number" value={form.purchase_price} onChange={e => setForm(f => ({...f, purchase_price: e.target.value === "" ? "" : parseFloat(e.target.value) || 0}))} className={inputCls} placeholder="e.g. 50,00,000" />
              </div>
              <div>
                <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Monthly EMI ₹</Label>
                <Input type="number" value={form.emi_amount} onChange={e => setForm(f => ({...f, emi_amount: e.target.value === "" ? "" : parseFloat(e.target.value) || 0}))} className={inputCls} placeholder="e.g. 35,000" />
              </div>
              {(form.listing_type === "short_term" || !form.listing_type) && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Price Floor ₹ <span className="font-normal text-slate-400">(min/night)</span></Label>
                    <Input type="number" value={form.min_price || ""} onChange={e => setForm(f => ({...f, min_price: parseFloat(e.target.value) || 0}))} className={inputCls} placeholder="e.g. 2,000" />
                  </div>
                  <div>
                    <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Price Ceiling ₹ <span className="font-normal text-slate-400">(max/night)</span></Label>
                    <Input type="number" value={form.max_price || ""} onChange={e => setForm(f => ({...f, max_price: parseFloat(e.target.value) || 0}))} className={inputCls} placeholder="e.g. 8,000" />
                  </div>
                </div>
              )}
              <div>
                <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Turnover Policy (after each checkout)</Label>
                <Select value={String(form.buffer_days ?? 0)} onValueChange={v => setForm(f => ({...f, buffer_days: Number(v)}))}>
                  <SelectTrigger className={inputCls + " mt-1"}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Same-day turnover — checkout day stays bookable</SelectItem>
                    <SelectItem value="1">Block 1 day after each checkout</SelectItem>
                    <SelectItem value="2">Block 2 days after each checkout</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-400 mt-1">
                  {(form.buffer_days ?? 0) === 0
                    ? "Guests can check in the same day another guest checks out — you handle cleaning between the checkout and check-in times."
                    : "Guests see the day(s) after every checkout as unavailable, giving you time to prepare."}
                </p>
                {(() => {
                  const ef = form.buffer_effective_from;
                  if (!ef || ef <= new Date().toISOString().slice(0, 10)) return null;
                  const month = new Date(ef + "T00:00:00").toLocaleDateString("en-IN", { month: "long", year: "numeric" });
                  return (
                    <div className="flex items-start gap-2 mt-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/40 rounded-lg px-3 py-2">
                      <WarningCircle size={13} weight="fill" className="text-amber-500 shrink-0 mt-0.5" />
                      <p className="text-[11px] text-amber-700 dark:text-amber-400 leading-snug">
                        <strong>Policy change in progress.</strong> Bookings already confirmed keep the previous {form.buffer_days_prev || 0}-day policy — no disruption. The {form.buffer_days || 0}-day gap applies to new stays from <strong>{month}</strong> onwards.
                      </p>
                    </div>
                  );
                })()}
              </div>
              {(form.buffer_days ?? 0) === 0 && (
                <div>
                  <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Minimum Turnover Gap (hours)</Label>
                  <Select value={String(form.min_turnover_hours ?? 0)} onValueChange={v => setForm(f => ({...f, min_turnover_hours: Number(v)}))}>
                    <SelectTrigger className={inputCls + " mt-1"}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">No minimum</SelectItem>
                      <SelectItem value="2">2 hours</SelectItem>
                      <SelectItem value="3">3 hours</SelectItem>
                      <SelectItem value="4">4 hours</SelectItem>
                      <SelectItem value="6">6 hours</SelectItem>
                      <SelectItem value="8">8 hours</SelectItem>
                    </SelectContent>
                  </Select>
                  {(() => {
                    const toMins = (t, dflt) => { const [h, m] = String(t || dflt).split(":"); return (parseInt(h) || 0) * 60 + (parseInt(m) || 0); };
                    const gapMins = toMins(form.checkin_time, "14:00") - toMins(form.checkout_time, "11:00");
                    const gapH = Math.floor(gapMins / 60); const gapM = gapMins % 60;
                    const gapLabel = `${gapH}h${gapM ? ` ${gapM}m` : ""}`;
                    const minH = form.min_turnover_hours ?? 0;
                    if (minH > 0 && gapMins < minH * 60) return (
                      <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1">
                        Your standard times leave only {gapLabel} between checkout ({form.checkout_time || "11:00"}) and check-in ({form.checkin_time || "14:00"}) — below your {minH}h minimum, so same-day check-ins will be blocked for guests.
                      </p>
                    );
                    return (
                      <p className="text-[11px] text-slate-400 mt-1">
                        Your standard times leave {gapLabel} between checkout and next check-in.{minH > 0 ? ` Same-day turnovers meet your ${minH}h minimum.` : ""}
                      </p>
                    );
                  })()}
                </div>
              )}
              <div className="flex items-center justify-between rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 px-3 py-2.5">
                <div>
                  <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">Instant Booking</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">{form.instant_booking ? "Guests book instantly, payment collected immediately" : "You review & approve each request before confirming"}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setForm(f => ({...f, instant_booking: !f.instant_booking}))}
                  className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${form.instant_booking ? "bg-amber-400" : "bg-slate-300 dark:bg-slate-600"}`}
                >
                  <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition-transform ${form.instant_booking ? "translate-x-4" : "translate-x-0"}`} />
                </button>
              </div>
              <div className="rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 px-3 py-2.5">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">Same-day booking cutoff</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">After this time, guests can't book a same-day check-in (no one to receive them at night). Leave blank to use your account default.</p>
                  </div>
                  <Input type="time" value={form.same_day_cutoff_time || ""} onChange={e => setForm(f => ({...f, same_day_cutoff_time: e.target.value}))} className={`${inputCls} w-28 shrink-0`} />
                </div>
                {form.same_day_cutoff_time && (
                  <button type="button" onClick={() => setForm(f => ({...f, same_day_cutoff_time: ""}))}
                    className="mt-1.5 text-[10px] text-slate-400 hover:text-amber-500">Clear (use account default)</button>
                )}
              </div>
            </div>
            {/* Guest Experience — WiFi, check-in instructions, house rules */}
            {(form.listing_type === "short_term" || !form.listing_type) && (
              <div className="space-y-3 rounded-xl border border-slate-200 dark:border-slate-600 p-3 bg-slate-50/50 dark:bg-slate-700/30">
                <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">Guest Experience <span className="text-slate-400 font-normal">(used by AI smart replies & auto-messages)</span></p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Check-in Time</Label>
                    <Input type="time" value={form.checkin_time || "14:00"} onChange={e => setForm(f => ({...f, checkin_time: e.target.value}))} className={inputCls} />
                  </div>
                  <div>
                    <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Checkout Time</Label>
                    <Input type="time" value={form.checkout_time || "11:00"} onChange={e => setForm(f => ({...f, checkout_time: e.target.value}))} className={inputCls} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">WiFi Name</Label>
                    <Input value={form.wifi_name || ""} onChange={e => setForm(f => ({...f, wifi_name: e.target.value}))} className={inputCls} placeholder="e.g. SeaViewGuest" />
                  </div>
                  <div>
                    <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">WiFi Password</Label>
                    <Input value={form.wifi_password || ""} onChange={e => setForm(f => ({...f, wifi_password: e.target.value}))} className={inputCls} placeholder="e.g. Goa2024" />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Check-in Instructions</Label>
                  <textarea value={form.checkin_instructions || ""} onChange={e => setForm(f => ({...f, checkin_instructions: e.target.value}))}
                    rows={2} placeholder="e.g. Key is in the lockbox at the main gate. Code: 1234. Turn left after the gate..."
                    className={inputCls + " resize-none w-full"} />
                </div>
                <div>
                  <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">House Rules</Label>
                  <textarea value={form.house_rules || ""} onChange={e => setForm(f => ({...f, house_rules: e.target.value}))}
                    rows={2} placeholder="e.g. No smoking indoors. Quiet hours after 10 PM. No parties."
                    className={inputCls + " resize-none w-full"} />
                </div>
                <div>
                  <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Airbnb Review Link <span className="text-slate-400 font-normal">(for post-checkout message)</span></Label>
                  <Input value={form.review_link || ""} onChange={e => setForm(f => ({...f, review_link: e.target.value}))} className={inputCls} placeholder="https://airbnb.com/..." />
                </div>
              </div>
            )}
            {/* Event Hosting */}
            {(form.listing_type === "short_term" || !form.listing_type) && (
              <div className="space-y-3 rounded-xl border border-slate-200 dark:border-slate-600 p-3 bg-slate-50/50 dark:bg-slate-700/30">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">Event Hosting</p>
                    <p className="text-[11px] text-slate-400">Allow photographers, yoga sessions &amp; events at your space</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setForm(f => ({...f, event_hosting_enabled: !f.event_hosting_enabled}))}
                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${form.event_hosting_enabled ? "bg-amber-400" : "bg-slate-300 dark:bg-slate-600"}`}
                  >
                    <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition-transform ${form.event_hosting_enabled ? "translate-x-4" : "translate-x-0"}`} />
                  </button>
                </div>
                {form.event_hosting_enabled && (
                  <>
                    <div>
                      <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Event Types</Label>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {["Photography shoot", "Yoga / fitness", "Birthday party", "Corporate offsite", "Wedding / pre-wedding", "Music session"].map(type => (
                          <button
                            key={type}
                            type="button"
                            onClick={() => {
                              const selected = (form.event_types || []).includes(type);
                              setForm(f => ({
                                ...f,
                                event_types: selected
                                  ? (f.event_types || []).filter(t => t !== type)
                                  : [...(f.event_types || []), type],
                                event_pricing: selected
                                  ? Object.fromEntries(Object.entries(f.event_pricing || {}).filter(([k]) => k !== type))
                                  : f.event_pricing || {},
                              }));
                            }}
                            className={`px-2.5 py-1 rounded-lg text-xs transition-all font-medium border ${
                              (form.event_types || []).includes(type)
                                ? "bg-amber-50 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400 border-amber-300 dark:border-amber-500/60"
                                : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-200 border-slate-200 dark:border-slate-600 hover:bg-slate-200 dark:hover:bg-slate-600"
                            }`}
                          >
                            {type}
                          </button>
                        ))}
                      </div>
                    </div>
                    {(form.event_types || []).length > 0 && (
                      <div>
                        <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Package Pricing ₹ <span className="font-normal text-slate-400">(flat rate per event)</span></Label>
                        <div className="mt-1.5 space-y-1.5">
                          {(form.event_types || []).map(type => (
                            <div key={type} className="flex items-center gap-2">
                              <span className="text-xs text-slate-600 dark:text-slate-300 flex-1 truncate">{type}</span>
                              <Input
                                type="number"
                                value={(form.event_pricing || {})[type] || ""}
                                onChange={e => setForm(f => ({
                                  ...f,
                                  event_pricing: { ...(f.event_pricing || {}), [type]: parseFloat(e.target.value) || 0 }
                                }))}
                                className={inputCls + " w-36 text-right"}
                                placeholder="e.g. 50,000"
                              />
                              <button
                                type="button"
                                onClick={() => setForm(f => ({
                                  ...f,
                                  event_types: (f.event_types || []).filter(t => t !== type),
                                  event_pricing: Object.fromEntries(Object.entries(f.event_pricing || {}).filter(([k]) => k !== type)),
                                }))}
                                className="text-slate-400 hover:text-red-500 text-xs shrink-0"
                              >✕</button>
                            </div>
                          ))}
                          <div className="flex items-center gap-2 pt-1 border-t border-slate-200 dark:border-slate-700">
                            <Input
                              value={customEventType}
                              onChange={e => setCustomEventType(e.target.value)}
                              className={inputCls + " flex-1"}
                              placeholder="Custom event type (e.g. Kitty party)"
                            />
                            <Input
                              type="number"
                              value={customEventPrice}
                              onChange={e => setCustomEventPrice(e.target.value)}
                              className={inputCls + " w-36 text-right"}
                              placeholder="₹ price"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const t = customEventType.trim();
                                if (!t) return;
                                const p = parseFloat(customEventPrice) || 0;
                                setForm(f => ({
                                  ...f,
                                  event_types: [...(f.event_types || []), t],
                                  event_pricing: { ...(f.event_pricing || {}), [t]: p },
                                }));
                                setCustomEventType(""); setCustomEventPrice("");
                              }}
                              className="text-xs bg-amber-400 hover:bg-amber-500 text-white rounded-lg px-3 py-1.5 font-semibold shrink-0"
                            >Add</button>
                          </div>
                        </div>
                      </div>
                    )}
                    <div>
                      <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Max Capacity</Label>
                      <Input type="number" value={form.event_capacity || ""} onChange={e => setForm(f => ({...f, event_capacity: parseInt(e.target.value) || 0}))} className={inputCls} placeholder="e.g. 20" />
                    </div>
                    <div>
                      <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Available Hours</Label>
                      <Input value={form.event_available_hours || ""} onChange={e => setForm(f => ({...f, event_available_hours: e.target.value}))} className={inputCls} placeholder="e.g. Weekdays 9am–6pm, Weekends 8am–8pm" />
                    </div>
                    <div>
                      <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Setup Notes</Label>
                      <textarea value={form.event_setup_notes || ""} onChange={e => setForm(f => ({...f, event_setup_notes: e.target.value}))}
                        rows={2} placeholder="e.g. Free parking for 5 cars. Terrace access included. AC available."
                        className={inputCls + " resize-none w-full"} />
                    </div>
                  </>
                )}
              </div>
            )}
            {/* Dynamic Pricing — only for short-term / Airbnb listings */}
            {(!form.listing_type || form.listing_type === "short_term") && (
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">Dynamic Pricing</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">Auto-adjust your nightly rate based on demand</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, dynamic_pricing_mode: f.dynamic_pricing_mode === "off" ? "hostfolio" : "off" }))}
                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none ${
                      form.dynamic_pricing_mode !== "off" ? "bg-amber-400" : "bg-slate-300 dark:bg-slate-600"
                    }`}
                  >
                    <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      form.dynamic_pricing_mode !== "off" ? "translate-x-4" : "translate-x-0"
                    }`} />
                  </button>
                </div>

                {form.dynamic_pricing_mode !== "off" && (
                  <div className="space-y-3 pt-1 border-t border-slate-200 dark:border-slate-700">
                    {/* Engine choice — segmented, one active */}
                    <div>
                      <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Pricing engine</Label>
                      <div className="grid grid-cols-2 gap-2 mt-1.5">
                        {[
                          { value: "hostfolio", title: "HostFolio AI", sub: "Included free" },
                          { value: "aiosell",   title: "Smart Pricing", sub: "+₹500/mo" },
                        ].map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setForm(f => ({ ...f, dynamic_pricing_mode: opt.value }))}
                            className={`text-left px-3 py-2 rounded-xl border text-xs transition-all ${
                              form.dynamic_pricing_mode === opt.value
                                ? "border-amber-400 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300"
                                : "border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 hover:border-slate-300"
                            }`}
                          >
                            <p className="font-semibold">{opt.title}</p>
                            <p className="text-[10px] text-slate-400 mt-0.5">{opt.sub}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Aiosell cost notice — shown the moment Smart Pricing is picked */}
                    {form.dynamic_pricing_mode === "aiosell" && (
                      editProp?.aiosell_pricing_status === "active" ? (
                        user?.smart_pricing_subscription_id ? (
                          <div className="flex items-start gap-2 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 px-3 py-2">
                            <span className="text-emerald-500 mt-0.5">✓</span>
                            <p className="text-[11px] text-emerald-700 dark:text-emerald-300">Smart Pricing is live. Our revenue engine manages your rates within the band below.</p>
                          </div>
                        ) : (
                          <div className="flex items-start gap-2 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 px-3 py-2">
                            <WarningCircle size={14} className="text-amber-500 shrink-0 mt-0.5" />
                            <p className="text-[11px] text-amber-700 dark:text-amber-300">
                              <strong>Approved — one step left.</strong> Smart Pricing starts optimising your rates once you set up auto-pay (₹500/mo per property).{" "}
                              <button type="button" onClick={() => navigate("/billing")} className="underline font-semibold hover:text-amber-800">Set up auto-pay →</button>
                            </p>
                          </div>
                        )
                      ) : editProp?.aiosell_pricing_status === "requested" ? (
                        <div className="flex items-start gap-2 rounded-xl bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-700 px-3 py-2">
                          <Spinner size={14} className="text-sky-500 shrink-0 mt-0.5" />
                          <p className="text-[11px] text-sky-700 dark:text-sky-300">Activation requested — our team is setting this up. You'll be emailed when it goes live (usually within a day).</p>
                        </div>
                      ) : (
                        <div className="flex items-start gap-2 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 px-3 py-2">
                          <WarningCircle size={14} className="text-amber-500 shrink-0 mt-0.5" />
                          <p className="text-[11px] text-amber-700 dark:text-amber-300"><strong>An additional ₹500/month per property</strong> applies for Smart Pricing. Saving will request activation — we'll set it up and confirm by email.</p>
                        </div>
                      )
                    )}

                    {/* Price band — single source of truth is Price Floor/Ceiling above; shown here read-only */}
                    <div className="rounded-xl bg-slate-50 dark:bg-slate-700/40 border border-slate-200 dark:border-slate-600 px-3 py-2.5">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-semibold text-slate-600 dark:text-slate-200">Price band (from Price Floor / Ceiling above)</span>
                        <span className="text-xs font-mono font-semibold text-slate-800 dark:text-white">
                          {form.min_price ? `₹${Number(form.min_price).toLocaleString("en-IN")}` : "—"} – {form.max_price ? `₹${Number(form.max_price).toLocaleString("en-IN")}` : "—"}
                        </span>
                      </div>
                      <p className="text-[10px] text-slate-400 mt-1">
                        {form.dynamic_pricing_mode === "aiosell"
                          ? "Rates stay within this band; the revenue engine sets the exact nightly price. Edit it in Price Floor / Ceiling above."
                          : "Our AI keeps suggested rates within this band on the Pricing page. Edit it in Price Floor / Ceiling above."}
                        {(!form.min_price || !form.max_price) && " Set the floor/ceiling above to define the band."}
                      </p>
                    </div>

                    {/* Aiosell demand profile — required by the revenue engine at setup */}
                    {form.dynamic_pricing_mode === "aiosell" && (
                      <div className="space-y-2.5 pt-2 border-t border-slate-200 dark:border-slate-700">
                        <div>
                          <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Guest type</Label>
                          <div className="flex gap-2 mt-1">
                            {["business", "leisure", "mix"].map(t => (
                              <button key={t} type="button"
                                onClick={() => setForm(f => ({ ...f, dp_property_type: t }))}
                                className={`flex-1 px-2 py-1.5 rounded-lg text-[11px] font-medium capitalize border transition-all ${
                                  form.dp_property_type === t
                                    ? "border-amber-400 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300"
                                    : "border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 hover:border-slate-300"
                                }`}>{t}</button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Peak months</Label>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map(m => {
                              const on = (form.dp_peak_months || []).includes(m);
                              return (
                                <button key={m} type="button"
                                  onClick={() => setForm(f => ({ ...f, dp_peak_months: on ? (f.dp_peak_months||[]).filter(x=>x!==m) : [...(f.dp_peak_months||[]), m] }))}
                                  className={`px-2 py-1 rounded-lg text-[10px] font-medium border transition-all ${
                                    on ? "border-amber-400 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300"
                                       : "border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 text-slate-500 dark:text-slate-300 hover:border-slate-300"
                                  }`}>{m}</button>
                              );
                            })}
                          </div>
                        </div>
                        <div>
                          <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Peak days of week</Label>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(d => {
                              const on = (form.dp_peak_days || []).includes(d);
                              return (
                                <button key={d} type="button"
                                  onClick={() => setForm(f => ({ ...f, dp_peak_days: on ? (f.dp_peak_days||[]).filter(x=>x!==d) : [...(f.dp_peak_days||[]), d] }))}
                                  className={`px-2 py-1 rounded-lg text-[10px] font-medium border transition-all ${
                                    on ? "border-amber-400 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300"
                                       : "border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 text-slate-500 dark:text-slate-300 hover:border-slate-300"
                                  }`}>{d}</button>
                              );
                            })}
                          </div>
                        </div>
                        <p className="text-[10px] text-slate-400">These help the revenue engine price your peak demand. Min price above is your floor threshold.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            <div>
              <Label className="text-slate-200 text-xs font-semibold mb-2 block">Amenities</Label>
              <div className="flex flex-wrap gap-2">
                {AMENITIES_LIST.map(a => (
                  <button key={a} onClick={() => toggleAmenity(a)} className={`px-2.5 py-1 rounded-lg text-xs transition-all font-medium border ${form.amenities.includes(a) ? "bg-amber-50 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400 border-amber-300 dark:border-amber-500/60" : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-200 border-slate-200 dark:border-slate-600 hover:bg-slate-200 dark:hover:bg-slate-600 hover:border-amber-300 dark:hover:border-amber-500/50"}`}>
                    {a}
                  </button>
                ))}
              </div>
            </div>
            {form.extra_amenities?.length > 0 && (
              <div>
                <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold mb-2 block">More Amenities <span className="font-normal text-slate-400">(imported from your Airbnb listing — shown to guests on your booking page)</span></Label>
                <div className="flex flex-wrap gap-1.5">
                  {form.extra_amenities.map((a, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] bg-teal-50 dark:bg-teal-500/10 text-teal-700 dark:text-teal-300 border border-teal-200 dark:border-teal-500/30">
                      {a}
                      <button onClick={() => setForm(f => ({ ...f, extra_amenities: f.extra_amenities.filter((_, j) => j !== i) }))} className="hover:text-red-500">
                        <X size={10} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div>
              <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold mb-2 block">Photos</Label>
              {form.photos?.length > 0 && (
                <div className="flex gap-2 flex-wrap mb-2">
                  {form.photos.map((url, i) => (
                    <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-700 group">
                      <img src={url} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => removePhoto(i)} className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm" data-testid={`remove-photo-${i}`}>
                        <X size={12} className="text-slate-200" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <label className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-slate-50 dark:bg-slate-700 border border-dashed border-slate-200 dark:border-slate-600 rounded-xl text-xs text-slate-500 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 cursor-pointer transition-colors" data-testid="photo-upload-label">
                  {uploading ? <><div className="spinner" style={{width:14,height:14}} /> Uploading...</> : <><UploadSimple size={14} /> Upload Photo</>}
                  <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" disabled={uploading} data-testid="photo-upload-input" />
                </label>
                <Input value={form._photoUrl || ""} onChange={e => setForm(f => ({...f, _photoUrl: e.target.value}))} className={inputCls + " flex-1"} placeholder="or paste URL" onKeyDown={e => {
                  if (e.key === 'Enter' && form._photoUrl) {
                    setForm(f => ({ ...f, photos: [...(f.photos || []), f._photoUrl], _photoUrl: "" }));
                  }
                }} />
              </div>
              <p className="text-slate-400 text-[10px] mt-1">Upload an image or paste a URL and press Enter</p>
            </div>
            <div>
              <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold mb-2 block">Videos</Label>
              {form.videos?.length > 0 && (
                <div className="flex gap-2 flex-wrap mb-2">
                  {form.videos.map((url, i) => (
                    <div key={i} className="relative w-32 h-20 rounded-lg overflow-hidden bg-slate-900 group">
                      <video src={url} preload="metadata" muted playsInline className="w-full h-full object-cover" />
                      <span className="absolute bottom-1 left-1 bg-black/60 text-white text-[9px] font-semibold px-1.5 py-0.5 rounded-full flex items-center gap-1"><VideoCamera size={9} weight="fill" /> Video {i + 1}</span>
                      <button onClick={() => removeVideo(i)} className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm" data-testid={`remove-video-${i}`}>
                        <X size={12} className="text-slate-700" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <label className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-slate-50 dark:bg-slate-700 border border-dashed border-slate-200 dark:border-slate-600 rounded-xl text-xs text-slate-500 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 cursor-pointer transition-colors" data-testid="video-upload-label">
                  {uploadingVideo ? <><div className="spinner" style={{width:14,height:14}} /> Uploading video...</> : <><VideoCamera size={14} /> Upload Video</>}
                  <input type="file" accept="video/mp4,video/quicktime,video/x-m4v,video/webm" onChange={handleVideoUpload} className="hidden" disabled={uploadingVideo} data-testid="video-upload-input" />
                </label>
                <Input value={form._videoUrl || ""} onChange={e => setForm(f => ({...f, _videoUrl: e.target.value}))} className={inputCls + " flex-1"} placeholder="or paste a video URL" onKeyDown={e => {
                  if (e.key === 'Enter' && form._videoUrl) {
                    e.preventDefault();
                    setForm(f => ({ ...f, videos: [...(f.videos || []), f._videoUrl], _videoUrl: "" }));
                  }
                }} />
              </div>
              <p className="text-slate-400 text-[10px] mt-1">A short walkthrough or tour, mp4/mov/webm up to 60 MB. Shown on your direct booking page under the photos.</p>
            </div>
          </div>
          <DialogFooter>
            <p className="text-[10px] text-slate-400 dark:text-slate-500 mr-auto self-center"><span className="text-red-500">*</span> Required fields</p>
            <button onClick={() => setDialogOpen(false)} className={btnCancel}>Cancel</button>
            <button onClick={handleSave} className={btnPrimary} data-testid="save-property-button">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Airbnb Listing Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 max-w-md shadow-xl">
          <DialogHeader>
            <DialogTitle className="font-manrope text-slate-900 dark:text-white flex items-center gap-2">
              <Link size={20} className="text-amber-500" /> Import Airbnb Listing
            </DialogTitle>
            <DialogDescription className="text-slate-500 dark:text-slate-400">Paste an Airbnb listing URL to auto-fill property details. We'll read the page directly; AI fills in anything we can't parse.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Airbnb Listing URL</Label>
              <Input value={importUrl} onChange={e => setImportUrl(e.target.value)} className={inputCls} placeholder="https://www.airbnb.co.in/rooms/..." data-testid="import-url-input" />
            </div>
            <p className="text-slate-400 text-xs">We'll fetch the listing page and extract details. If the page is unavailable, AI will estimate from the URL.</p>
          </div>
          <DialogFooter>
            <button onClick={() => setImportDialogOpen(false)} className={btnCancel}>Cancel</button>
            <button onClick={handleImportListing} disabled={importing} className={btnPrimary + " disabled:opacity-50 flex items-center gap-2"} data-testid="import-submit-button">
              {importing ? <><div className="spinner" style={{width:14,height:14}} /> Importing...</> : "Import & Fill"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Property Detail Dialog */}
      <Dialog open={!!detailProp} onOpenChange={(v) => { if (!v) setDetailProp(null); }}>
        <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 max-w-2xl max-h-[85vh] overflow-y-auto shadow-xl">
          <DialogHeader>
            <DialogTitle className="font-manrope text-slate-900 dark:text-white flex items-center gap-3">
              {detailProp?.name}
              <Badge variant="outline" className={
                detailProp?.status === "active" ? "bg-emerald-50 text-emerald-600 border-emerald-200" :
                detailProp?.status === "maintenance" ? "bg-amber-50 text-amber-600 border-amber-200" :
                "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600"
              }>{detailProp?.status}</Badge>
            </DialogTitle>
            <DialogDescription className="text-slate-500 dark:text-slate-400">{detailProp?.city}, {detailProp?.property_type} - {detailProp?.bedrooms} BR / {detailProp?.bathrooms} BA</DialogDescription>
          </DialogHeader>

          {financials && (
            <Tabs defaultValue={detailProp?.unit_type === "hotel" ? "rooms" : "financials"} className="mt-2">
              <TabsList className="bg-slate-100 dark:bg-slate-700 w-full">
                {detailProp?.unit_type === "hotel" && (
                  <TabsTrigger value="rooms" className="flex-1 text-xs text-slate-600 dark:text-slate-300 data-[state=active]:bg-amber-400 data-[state=active]:text-white">Rooms</TabsTrigger>
                )}
                <TabsTrigger value="financials" className="flex-1 text-xs text-slate-600 dark:text-slate-300 data-[state=active]:bg-amber-400 data-[state=active]:text-white">Financials</TabsTrigger>
                {!hideRevenue && <TabsTrigger value="chart" className="flex-1 text-xs text-slate-600 dark:text-slate-300 data-[state=active]:bg-amber-400 data-[state=active]:text-white">Chart</TabsTrigger>}
                <TabsTrigger value="details" className="flex-1 text-xs text-slate-600 dark:text-slate-300 data-[state=active]:bg-amber-400 data-[state=active]:text-white">Details</TabsTrigger>
              </TabsList>

              {detailProp?.unit_type === "hotel" && (
                <TabsContent value="rooms" className="mt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-slate-500 dark:text-slate-400">{detailRooms.length} room{detailRooms.length !== 1 ? "s" : ""} — drag arrows to reorder</p>
                    <button onClick={openAddRoom} className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-xl bg-amber-400 hover:bg-amber-500 text-white transition">
                      <Plus size={12} weight="bold" /> Add Room
                    </button>
                  </div>
                  {detailRooms.length === 0 ? (
                    <div className="text-center py-8 text-slate-400 text-sm">
                      <Buildings size={32} className="mx-auto mb-2 text-slate-300" />
                      No rooms yet. Add your first room.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {detailRooms.map((r, idx) => (
                        <div key={r.room_id} className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50">
                          <div className="flex flex-col gap-0.5">
                            <button
                              disabled={idx === 0}
                              onClick={() => {
                                const reordered = [...detailRooms];
                                [reordered[idx - 1], reordered[idx]] = [reordered[idx], reordered[idx - 1]];
                                handleRoomReorder(reordered);
                              }}
                              className="p-0.5 rounded text-slate-400 hover:text-slate-600 disabled:opacity-20 disabled:cursor-not-allowed transition"
                            >
                              <ArrowUp size={12} />
                            </button>
                            <button
                              disabled={idx === detailRooms.length - 1}
                              onClick={() => {
                                const reordered = [...detailRooms];
                                [reordered[idx + 1], reordered[idx]] = [reordered[idx], reordered[idx + 1]];
                                handleRoomReorder(reordered);
                              }}
                              className="p-0.5 rounded text-slate-400 hover:text-slate-600 disabled:opacity-20 disabled:cursor-not-allowed transition"
                            >
                              <ArrowDown size={12} />
                            </button>
                          </div>
                          {r.photos?.[0] ? (
                            <img src={r.photos[0]} alt={r.name} className="w-12 h-12 rounded-lg object-cover shrink-0" />
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-slate-200 dark:bg-slate-600 flex items-center justify-center shrink-0">
                              <Bed size={18} className="text-slate-400" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-slate-800 dark:text-white truncate">{r.name}</p>
                            <p className="text-xs text-slate-400 mt-0.5">
                              {r.bedrooms} BR · {r.bathrooms} BA · {r.max_guests} guests
                              {r.nightly_rate > 0 && ` · ${formatINR(r.nightly_rate)}/night`}
                            </p>
                            {r.aiosell_room_code && (
                              <p className="text-[10px] text-slate-400 mt-0.5">Channel: {r.aiosell_room_code}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              onClick={() => setGuidebookRoom({ room: r, prop: detailProp })}
                              className="p-1.5 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/20 text-amber-500 hover:text-amber-600 transition"
                              title="Guidebook"
                            >
                              <BookOpen size={14} />
                            </button>
                            <button onClick={() => openEditRoom(r)} className="p-1.5 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-500 transition">
                              <PencilSimple size={14} />
                            </button>
                            <button onClick={() => handleDeleteRoom(r.room_id)} className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-400 hover:text-red-500 transition">
                              <Trash size={14} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              )}
              <TabsContent value="financials" className="space-y-3 mt-4">
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: "Total Revenue", value: formatINR(financials.total_revenue), color: "text-amber-500" },
                    { label: "Total Expenses", value: formatINR(financials.total_expenses), color: "text-red-500" },
                    { label: "Net Profit", value: formatINR(financials.net_profit), color: financials.net_profit >= 0 ? "text-emerald-500" : "text-red-500" },
                    { label: "ROI", value: `${financials.roi}%`, color: "text-sky-500" },
                    { label: "Occupancy", value: `${financials.occupancy}%`, color: "text-emerald-500" },
                    { label: "ADR", value: formatINR(financials.adr), color: "text-amber-500" },
                  ].filter(s => !hideRevenue || ["Total Expenses", "Occupancy"].includes(s.label)).map(s => (
                    <div key={s.label} className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-3 border border-slate-100 dark:border-slate-600">
                      <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{s.label}</p>
                      <p className={`font-mono text-lg font-semibold mt-1 ${s.color}`}>{s.value}</p>
                    </div>
                  ))}
                </div>
              </TabsContent>
              {!hideRevenue && (
              <TabsContent value="chart" className="mt-4">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={financials.monthly_data} barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="month" stroke="#94a3b8" fontSize={10} tickLine={false} />
                    <YAxis stroke="#94a3b8" fontSize={10} tickLine={false} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(v, name) => [formatINR(v), name === "revenue" ? "Revenue" : "Expenses"]} />
                    <Legend formatter={(v) => v === "revenue" ? "Revenue" : "Expenses"} wrapperStyle={{ fontSize: "11px" }} />
                    <Bar dataKey="revenue" name="revenue" fill="#E8B931" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="expenses" name="expenses" fill="#94a3b8" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </TabsContent>
              )}
              <TabsContent value="details" className="mt-4 space-y-3">
                <div className="text-sm text-slate-700 dark:text-slate-200">
                  <p><span className="text-slate-500 dark:text-slate-400">Address:</span> {detailProp?.address}</p>
                  {!hideRevenue && (<>
                  <p className="mt-1"><span className="text-slate-500 dark:text-slate-400">Mortgage:</span> {formatINR(detailProp?.mortgage_amount || 0)}</p>
                  <p className="mt-1"><span className="text-slate-500 dark:text-slate-400">EMI:</span> {formatINR(detailProp?.emi_amount || 0)}/mo</p>
                  <p className="mt-1"><span className="text-slate-500 dark:text-slate-400">Purchase Price:</span> {formatINR(detailProp?.purchase_price || 0)}</p>
                  </>)}
                </div>
                {detailProp?.photos?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-widest mb-2">Photos</p>
                    <div className="flex gap-2 flex-wrap">
                      {detailProp.photos.map((url, i) => (
                        <div key={i} className="w-24 h-24 rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-700">
                          <img src={url} alt={`Photo ${i+1}`} className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {detailProp?.amenities?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-widest mb-2">Amenities</p>
                    <div className="flex flex-wrap gap-1.5">
                      {detailProp.amenities.map(a => (
                        <Badge key={a} variant="outline" className="bg-slate-50 dark:bg-slate-700 text-slate-600 dark:text-slate-200 border-slate-200 dark:border-slate-600 text-xs">{a}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter className="mt-4">
            <button onClick={() => { setDetailProp(null); openEdit(detailProp); }} className="bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600 font-medium rounded-xl px-4 py-2 text-sm flex items-center gap-1.5 transition-colors" data-testid="edit-property-button">
              <PencilSimple size={14} /> Edit
            </button>
            <button onClick={() => handleDelete(detailProp?.id)} className="bg-red-50 text-red-500 hover:bg-red-100 font-medium rounded-xl px-4 py-2 text-sm flex items-center gap-1.5 transition-colors" data-testid="delete-property-button">
              <Trash size={14} /> Delete
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Guidebook Editor Dialog */}
      {guidebookProp && <GuidebookEditor prop={guidebookProp} onClose={() => setGuidebookProp(null)} />}
      {guidebookRoom && (
        <GuidebookEditor
          prop={guidebookRoom.prop}
          room={guidebookRoom.room}
          onClose={() => setGuidebookRoom(null)}
        />
      )}

      {/* Export listing kit (for listing on other OTAs) */}
      {exportProp && <ExportKitDialog prop={exportProp} onClose={() => setExportProp(null)} />}

      {/* Add / Edit Room Dialog */}
      <Dialog open={roomDialogOpen} onOpenChange={setRoomDialogOpen}>
        <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 max-w-md max-h-[85vh] overflow-y-auto shadow-xl">
          <DialogHeader>
            <DialogTitle className="font-manrope text-slate-900 dark:text-white">{editRoom ? "Edit Room" : "Add Room"}</DialogTitle>
            <DialogDescription className="text-slate-500 dark:text-slate-400">Configure this room's details and pricing.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Room Name <span className="text-red-500">*</span></Label>
              <Input value={roomForm.name} onChange={e => setRoomForm(f => ({ ...f, name: e.target.value }))} className={inputCls} placeholder="e.g. Deluxe King Room" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Bedrooms</Label>
                <Input type="number" min={1} value={roomForm.bedrooms} onChange={e => setRoomForm(f => ({ ...f, bedrooms: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Bathrooms</Label>
                <Input type="number" min={1} value={roomForm.bathrooms} onChange={e => setRoomForm(f => ({ ...f, bathrooms: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Max Guests</Label>
                <Input type="number" min={1} value={roomForm.max_guests} onChange={e => setRoomForm(f => ({ ...f, max_guests: e.target.value }))} className={inputCls} />
              </div>
            </div>
            <div>
              <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Nightly Rate ₹</Label>
              <Input type="number" value={roomForm.nightly_rate || ""} onChange={e => setRoomForm(f => ({ ...f, nightly_rate: e.target.value }))} className={inputCls} placeholder="e.g. 3,500" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Channel Manager Room Code</Label>
                <Input value={roomForm.aiosell_room_code || ""} onChange={e => setRoomForm(f => ({ ...f, aiosell_room_code: e.target.value }))} className={inputCls} placeholder="e.g. EXECUTIVE" />
              </div>
              <div>
                <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Rate Plan Code</Label>
                <Input value={roomForm.aiosell_rate_plan_code || ""} onChange={e => setRoomForm(f => ({ ...f, aiosell_rate_plan_code: e.target.value }))} className={inputCls} placeholder="e.g. BAR" />
              </div>
              <p className="col-span-2 text-[10px] text-slate-400 -mt-1">Room Code + Rate Plan Code are used to sync rates &amp; availability to all OTAs via the channel manager</p>
            </div>
            <div>
              <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Check-in Instructions</Label>
              <textarea value={roomForm.checkin_instructions || ""} onChange={e => setRoomForm(f => ({ ...f, checkin_instructions: e.target.value }))}
                rows={2} placeholder="Room-specific instructions (e.g. Room 204, second floor, key is at reception)"
                className={inputCls + " resize-none w-full"} />
            </div>
            <div>
              <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Check-in Video URL <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input value={roomForm.checkin_video_url || ""} onChange={e => setRoomForm(f => ({ ...f, checkin_video_url: e.target.value }))}
                className={inputCls} placeholder="YouTube or Google Drive link" />
              <p className="text-[10px] text-slate-400 mt-0.5">Shown to guests as a "Watch check-in video" button in their guidebook</p>
            </div>
            <div>
              <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold mb-2 block">Room Photos</Label>
              {roomForm.photos?.length > 0 && (
                <div className="flex gap-2 flex-wrap mb-2">
                  {roomForm.photos.map((url, i) => (
                    <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-700 group">
                      <img src={url} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => setRoomForm(f => ({ ...f, photos: f.photos.filter((_, j) => j !== i) }))}
                        className="absolute top-0.5 right-0.5 bg-white/90 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm">
                        <X size={10} className="text-slate-600" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <label className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-dashed border-slate-200 dark:border-slate-600 rounded-xl text-xs text-slate-500 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 cursor-pointer transition-colors">
                  {roomUploading ? <><div className="spinner" style={{width:12,height:12}} /> Uploading...</> : <><UploadSimple size={12} /> Upload</>}
                  <input type="file" accept="image/*" onChange={handleRoomPhotoUpload} className="hidden" disabled={roomUploading} />
                </label>
                <Input value={roomForm._photoUrl || ""} onChange={e => setRoomForm(f => ({ ...f, _photoUrl: e.target.value }))} className={inputCls + " flex-1"} placeholder="or paste URL"
                  onKeyDown={e => {
                    if (e.key === "Enter" && roomForm._photoUrl) {
                      setRoomForm(f => ({ ...f, photos: [...(f.photos || []), f._photoUrl], _photoUrl: "" }));
                    }
                  }} />
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <button onClick={() => setRoomDialogOpen(false)} className={btnCancel}>Cancel</button>
            <button onClick={handleSaveRoom} className={btnPrimary}>Save Room</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const BADGE_OPTIONS = ["none", "Host's Pick", "Must Try", "Hidden Gem", "Local Fave"];
const BADGE_STYLES = {
  "Host's Pick": "bg-amber-100 text-amber-700 border-amber-200",
  "Must Try":    "bg-green-100 text-green-700 border-green-200",
  "Hidden Gem":  "bg-purple-100 text-purple-700 border-purple-200",
  "Local Fave":  "bg-blue-100 text-blue-700 border-blue-200",
};

function ExportKitDialog({ prop, onClose }) {
  const pid = prop?.id || prop?.property_id;
  const [text, setText] = useState("");
  const [photoCount, setPhotoCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!pid) return;
    setLoading(true);
    api.get(`/export/property/${pid}/listing-text`)
      .then(res => { setText(res.data.text || ""); setPhotoCount(res.data.photo_count || 0); })
      .catch(() => toast.error("Could not load listing content"))
      .finally(() => setLoading(false));
  }, [pid]);

  const copy = () => {
    navigator.clipboard.writeText(text)
      .then(() => { setCopied(true); toast.success("Listing details copied!"); setTimeout(() => setCopied(false), 2000); })
      .catch(() => toast.error("Could not copy"));
  };

  const downloadZip = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/export/property/${pid}/listing-kit`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const safe = (prop.name || "property").replace(/[^A-Za-z0-9._-]+/g, "_").slice(0, 40) || "property";
      a.href = url; a.download = `${safe}_listing_kit.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Listing kit downloaded");
    } catch {
      toast.error("Could not download listing kit");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={!!prop} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 max-w-lg max-h-[85vh] overflow-y-auto shadow-xl">
        <DialogHeader>
          <DialogTitle className="font-manrope text-slate-900 dark:text-white">Export for other OTAs</DialogTitle>
          <DialogDescription className="text-slate-500 dark:text-slate-400">
            Copy the details or download a ZIP (details {photoCount ? `+ ${photoCount} photo${photoCount === 1 ? "" : "s"}` : ""}) to list {prop?.name} on Vrbo, Booking.com and more.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <textarea
            readOnly
            value={loading ? "Loading…" : text}
            className="w-full h-64 text-xs font-mono rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 text-slate-700 dark:text-slate-200 p-3 resize-none"
          />
          <div className="flex gap-2">
            <button onClick={copy} disabled={loading}
              className="flex-1 text-xs font-semibold py-2 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-600 transition disabled:opacity-50">
              {copied ? "Copied ✓" : "Copy details"}
            </button>
            <button onClick={downloadZip} disabled={downloading || loading}
              className="flex-1 text-xs font-semibold py-2 rounded-xl bg-teal-600 hover:bg-teal-700 text-white transition disabled:opacity-50 flex items-center justify-center gap-1.5">
              <DownloadSimple size={14} /> {downloading ? "Preparing…" : `Download ZIP${photoCount ? ` (+${photoCount} photos)` : ""}`}
            </button>
          </div>
          <p className="text-[11px] text-slate-400">Paste the text into the OTA's listing form and upload the photos from the ZIP. OTAs run their own review before a listing goes live; ongoing rate and availability sync is handled by HostFolio's channel manager.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function GuidebookEditor({ prop, room, onClose }) {
  const inputCls = "mt-1 h-8 text-sm bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 rounded-lg focus:ring-amber-400 focus:border-amber-400";
  const [tab, setTab] = useState("overview");
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (room) {
      setData({ ...room });
    } else {
      api.get(`/properties/${prop.id}/guidebook`).then(r => setData(r.data)).catch(() => setData({}));
    }
  }, [prop.id, room]);

  const save = async () => {
    setSaving(true);
    try {
      if (room) {
        await api.put(`/rooms/${room.room_id}`, data);
      } else {
        await api.put(`/properties/${prop.id}/guidebook`, data);
      }
      toast.success("Guidebook saved!");
      onClose();
    } catch { toast.error("Failed to save guidebook"); } finally { setSaving(false); }
  };

  const addRestaurant = () => setData(d => ({ ...d, restaurants: [...(d.restaurants || []), { name: "", cuisine: "", price_range: "₹₹", maps_link: "", host_note: "", badge: "" }] }));
  const removeRestaurant = i => setData(d => ({ ...d, restaurants: d.restaurants.filter((_, idx) => idx !== i) }));
  const updateRestaurant = (i, field, value) => setData(d => ({ ...d, restaurants: d.restaurants.map((r, idx) => idx === i ? { ...r, [field]: value } : r) }));

  const addPlace = () => setData(d => ({ ...d, places: [...(d.places || []), { name: "", description: "", maps_link: "", badge: "" }] }));
  const removePlace = i => setData(d => ({ ...d, places: d.places.filter((_, idx) => idx !== i) }));
  const updatePlace = (i, field, value) => setData(d => ({ ...d, places: d.places.map((p, idx) => idx === i ? { ...p, [field]: value } : p) }));

  const suggestPlaces = async () => {
    setSuggesting(true);
    try {
      const r = await api.get(`/properties/${prop.id}/guidebook/suggest`);
      setData(d => ({
        ...d,
        restaurants: [...(d.restaurants || []), ...r.data.restaurants],
        places:       [...(d.places || []),       ...r.data.places],
      }));
      toast.success(`Added ${r.data.restaurants.length} restaurants and ${r.data.places.length} attractions — edit and save when ready.`);
      setTab("restaurants");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI suggestion failed");
    } finally { setSuggesting(false); }
  };

  const updateContact = (i, field, value) => setData(d => ({ ...d, emergency_contacts: d.emergency_contacts.map((c, idx) => idx === i ? { ...c, [field]: value } : c) }));

  if (!data) return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 max-w-xl">
        <div className="flex items-center justify-center py-8"><div className="spinner" /></div>
      </DialogContent>
    </Dialog>
  );

  const contacts = data.emergency_contacts?.length > 0 ? data.emergency_contacts : [
    { label: "Hospital", name: "", phone: "" },
    { label: "Police", name: "Local Police Station", phone: "100" },
    { label: "Ambulance", name: "Emergency Ambulance", phone: "108" },
  ];

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 max-w-xl max-h-[85vh] overflow-y-auto shadow-xl">
        <DialogHeader>
          <DialogTitle className="font-manrope text-slate-900 dark:text-white flex items-center gap-2">
            <BookOpen size={18} className="text-amber-500" />
            {room ? `${prop.name} — ${room.name}` : `Guest Guidebook — ${prop.name}`}
          </DialogTitle>
          <DialogDescription className="text-slate-500 dark:text-slate-400">
            {room ? "Per-room guidebook and check-in info sent to guests via WhatsApp." : "Guests receive this as a WhatsApp link when they book."}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="w-full mt-2">
          <TabsList className="grid grid-cols-5 w-full bg-slate-100 dark:bg-slate-700 rounded-xl p-1">
            {[
              { value: "overview", label: "Overview" },
              { value: "restaurants", label: "Eat" },
              { value: "places", label: "Visit" },
              { value: "emergency", label: "Emergency" },
              { value: "messages", label: "Messages" },
            ].map(t => (
              <TabsTrigger key={t.value} value={t.value} className="rounded-lg text-xs data-[state=active]:bg-white data-[state=active]:dark:bg-slate-600 data-[state=active]:shadow-sm">{t.label}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="overview" className="space-y-3 mt-3">
            <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600">
              <div>
                <p className="text-sm font-semibold text-slate-800 dark:text-white">Enable Guidebook</p>
                <p className="text-xs text-slate-400">Send guidebook link to guests via WhatsApp on booking</p>
              </div>
              <button
                type="button"
                onClick={() => setData(d => ({ ...d, guidebook_enabled: !d.guidebook_enabled }))}
                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${data.guidebook_enabled ? "bg-amber-400" : "bg-slate-300 dark:bg-slate-600"}`}
              >
                <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition-transform ${data.guidebook_enabled ? "translate-x-4" : "translate-x-0"}`} />
              </button>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">Check-in info, WiFi, and house rules are pulled from your property settings automatically.</p>

            {/* Guidebook style template picker */}
            <div>
              <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Guidebook Style</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                {[
                  { key: "classic", label: "Classic",  gradient: "from-amber-500 to-amber-600",   desc: "Warm amber" },
                  { key: "minimal", label: "Minimal",  gradient: "from-slate-700 to-slate-800",   desc: "Clean slate" },
                  { key: "luxury",  label: "Luxury",   gradient: "from-yellow-900 to-yellow-950", desc: "Dark gold" },
                  { key: "cozy",    label: "Cozy",     gradient: "from-rose-500 to-rose-600",     desc: "Soft rose" },
                ].map(t => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setData(d => ({ ...d, guidebook_template: t.key }))}
                    className={`flex items-center gap-2.5 p-2.5 rounded-xl border-2 text-left transition ${
                      (data.guidebook_template || "classic") === t.key
                        ? "border-amber-400 bg-amber-50 dark:bg-amber-950/20"
                        : "border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500"
                    }`}
                  >
                    <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${t.gradient} shrink-0`} />
                    <div>
                      <p className="text-xs font-semibold text-slate-800 dark:text-white">{t.label}</p>
                      <p className="text-[10px] text-slate-400">{t.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={suggestPlaces}
              disabled={suggesting}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-dashed border-amber-300 bg-amber-50 dark:bg-amber-950/20 text-amber-700 dark:text-amber-400 text-sm font-semibold hover:bg-amber-100 transition disabled:opacity-50"
            >
              {suggesting ? <Spinner size={14} className="animate-spin" /> : "✨"}
              {suggesting ? "Generating suggestions…" : `AI-suggest restaurants & attractions near ${[prop.neighbourhood, prop.pincode, prop.city].filter(Boolean).join(", ") || "this property"}`}
            </button>
            <div>
              <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Things Guests Should Know</Label>
              <p className="text-[11px] text-slate-400 mb-1">One tip per line — nearest parking, trash day, pool rules, etc.</p>
              <textarea
                value={(data.things_to_know || []).join("\n")}
                onChange={e => setData(d => ({ ...d, things_to_know: e.target.value.split("\n") }))}
                rows={4} placeholder={"Nearest parking: Street parking on Main Rd (free after 8pm)\nTrash pickup: Tuesday mornings\nPool hours: 7am–9pm"}
                className={inputCls + " resize-none w-full h-auto"} style={{ height: "auto", minHeight: "80px" }}
              />
            </div>
          </TabsContent>

          <TabsContent value="restaurants" className="space-y-3 mt-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">Show restaurants section</p>
                <button
                  type="button"
                  onClick={() => setData(d => ({ ...d, restaurants_enabled: !d.restaurants_enabled }))}
                  className={`relative inline-flex h-4 w-8 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${data.restaurants_enabled !== false ? "bg-amber-400" : "bg-slate-300 dark:bg-slate-600"}`}
                >
                  <span className={`pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow ring-0 transition-transform ${data.restaurants_enabled !== false ? "translate-x-4" : "translate-x-0"}`} />
                </button>
              </div>
              <button onClick={addRestaurant} className="text-xs px-2.5 py-1 rounded-lg bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-700 font-semibold hover:bg-amber-100 transition">+ Add</button>
            </div>
            {(data.restaurants || []).map((r, i) => (
              <div key={i} className="p-3 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 space-y-2">
                <div className="flex gap-2">
                  <Input value={r.name} onChange={e => updateRestaurant(i, "name", e.target.value)} className={inputCls + " flex-1"} placeholder="Restaurant name" />
                  <Input value={r.cuisine} onChange={e => updateRestaurant(i, "cuisine", e.target.value)} className={inputCls + " w-28"} placeholder="Cuisine" />
                  <Select value={r.price_range || "₹₹"} onValueChange={v => updateRestaurant(i, "price_range", v)}>
                    <SelectTrigger className={inputCls + " w-16"}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white dark:bg-slate-800 border-slate-200">
                      {["₹", "₹₹", "₹₹₹"].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Input value={r.maps_link} onChange={e => updateRestaurant(i, "maps_link", e.target.value)} className={inputCls} placeholder="Google Maps link" />
                <div className="flex gap-2 items-center">
                  <Input value={r.host_note} onChange={e => updateRestaurant(i, "host_note", e.target.value)} className={inputCls + " flex-1"} placeholder="Host note (e.g. Try the butter naan!)" />
                  <Select value={r.badge || "none"} onValueChange={v => updateRestaurant(i, "badge", v === "none" ? "" : v)}>
                    <SelectTrigger className={inputCls + " w-32 shrink-0"}>
                      <SelectValue placeholder="Badge" />
                    </SelectTrigger>
                    <SelectContent className="bg-white dark:bg-slate-800 border-slate-200">
                      {BADGE_OPTIONS.map(b => <SelectItem key={b} value={b}>{b === "none" ? "No badge" : b}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <button onClick={() => removeRestaurant(i)} className="text-red-400 hover:text-red-600 p-1 shrink-0"><X size={14} /></button>
                </div>
                {r.badge && (
                  <p className="text-[10px] text-slate-400">Guests will see <span className={`font-semibold px-1.5 py-0.5 rounded-full border text-[10px] ${BADGE_STYLES[r.badge] || ""}`}>{r.badge}</span> badge on this restaurant.</p>
                )}
              </div>
            ))}
            {(data.restaurants || []).length === 0 && <p className="text-xs text-slate-400 text-center py-4">No restaurants added yet. Use "✨ AI-suggest" on the Overview tab or click "+ Add".</p>}
          </TabsContent>

          <TabsContent value="places" className="space-y-3 mt-3">
            <div className="flex justify-end">
              <button onClick={addPlace} className="text-xs px-2.5 py-1 rounded-lg bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-700 font-semibold hover:bg-amber-100 transition">+ Add</button>
            </div>
            {(data.places || []).map((p, i) => (
              <div key={i} className="p-3 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 space-y-2">
                <div className="flex gap-2">
                  <Input value={p.name} onChange={e => updatePlace(i, "name", e.target.value)} className={inputCls + " flex-1"} placeholder="Place name (e.g. Anjuna Beach)" />
                  <Select value={p.badge || "none"} onValueChange={v => updatePlace(i, "badge", v === "none" ? "" : v)}>
                    <SelectTrigger className={inputCls + " w-32 shrink-0"}>
                      <SelectValue placeholder="Badge" />
                    </SelectTrigger>
                    <SelectContent className="bg-white dark:bg-slate-800 border-slate-200">
                      {BADGE_OPTIONS.map(b => <SelectItem key={b} value={b}>{b === "none" ? "No badge" : b}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <button onClick={() => removePlace(i)} className="text-red-400 hover:text-red-600 p-1 shrink-0"><X size={14} /></button>
                </div>
                <Input value={p.maps_link} onChange={e => updatePlace(i, "maps_link", e.target.value)} className={inputCls} placeholder="Google Maps link" />
                <Input value={p.description} onChange={e => updatePlace(i, "description", e.target.value)} className={inputCls} placeholder="Brief description (e.g. 10 min walk, great sunset views)" />
                {p.badge && (
                  <p className="text-[10px] text-slate-400">Guests will see <span className={`font-semibold px-1.5 py-0.5 rounded-full border text-[10px] ${BADGE_STYLES[p.badge] || ""}`}>{p.badge}</span> badge on this place.</p>
                )}
              </div>
            ))}
            {(data.places || []).length === 0 && <p className="text-xs text-slate-400 text-center py-4">No places added yet. Use "✨ AI-suggest" on the Overview tab or click "+ Add".</p>}
          </TabsContent>

          <TabsContent value="emergency" className="space-y-3 mt-3">
            <p className="text-xs text-slate-500 dark:text-slate-400">These contacts are shown at the bottom of the guest guidebook.</p>
            {contacts.map((c, i) => (
              <div key={i} className="p-3 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 space-y-2">
                <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">{c.label}</p>
                <div className="grid grid-cols-2 gap-2">
                  <Input value={c.name} onChange={e => updateContact(i, "name", e.target.value)} className={inputCls} placeholder="Name / place" />
                  <Input value={c.phone} onChange={e => updateContact(i, "phone", e.target.value)} className={inputCls} placeholder="Phone number" />
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="messages" className="space-y-4 mt-3">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {room
                ? "Customize WhatsApp messages for this specific room. Leave blank to inherit from the property-level template."
                : "Customize the WhatsApp messages sent to guests for this property. Leave blank to use the global default."}
            </p>
            <div className="text-[11px] text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-700/50 rounded-lg px-3 py-2 leading-relaxed">
              <span className="font-semibold text-slate-700 dark:text-slate-300">Available variables: </span>
              {["{guest_name}", "{property_name}", "{check_in}", "{check_out}", "{checkin_time}", "{checkout_time}", "{guidebook_link}", "{review_link}"].map(v => (
                <code key={v} className="mx-0.5 px-1 py-0.5 rounded bg-white dark:bg-slate-700 text-amber-600 dark:text-amber-400 border border-slate-200 dark:border-slate-600">{v}</code>
              ))}
            </div>
            {[
              { key: "wa_template_booking_confirmed", icon: "✅", label: "Booking Confirmed",   desc: "Sent immediately when booking is confirmed" },
              { key: "wa_template_pre_arrival",       icon: "🗓️", label: "Pre-arrival (48h)",   desc: "Sent 48 hours before check-in" },
              { key: "wa_template_welcome",           icon: "🏡", label: "Welcome Message",     desc: "Sent on the morning of check-in day" },
              { key: "wa_template_checkout",          icon: "🧳", label: "Checkout Reminder",   desc: "Sent on the day of checkout" },
              { key: "wa_template_review",            icon: "⭐", label: "Review Request",      desc: "Sent on checkout day asking for a review" },
            ].map(t => (
              <div key={t.key} className="space-y-1">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm leading-none">{t.icon}</span>
                  <p className="text-xs font-semibold text-slate-800 dark:text-white">{t.label}</p>
                </div>
                <p className="text-[10px] text-slate-400">{t.desc}</p>
                <textarea
                  value={data[t.key] || ""}
                  onChange={e => setData(d => ({ ...d, [t.key]: e.target.value }))}
                  rows={3}
                  placeholder="Leave blank to use default message"
                  className={inputCls + " resize-none w-full text-xs leading-relaxed"}
                  style={{ minHeight: "64px", height: "auto" }}
                />
              </div>
            ))}
          </TabsContent>
        </Tabs>

        <div className="flex gap-2 mt-4">
          {!room && data.guidebook_enabled && (
            <a
              href={`/guide/preview-${prop.id}`}
              target="_blank"
              rel="noreferrer"
              className="flex-1 text-sm font-medium py-2 rounded-xl border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition text-center"
            >
              Preview
            </a>
          )}
          <button onClick={save} disabled={saving} className="flex-1 text-sm font-semibold py-2 rounded-xl bg-amber-400 hover:bg-amber-500 text-white transition disabled:opacity-60">
            {saving ? "Saving…" : "Save Guidebook"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function defaultRoomForm() {
  return {
    name: "", nightly_rate: 0,
    bedrooms: 1, bathrooms: 1, max_guests: 2,
    photos: [], checkin_instructions: "", checkin_video_url: "",
    aiosell_room_code: "", aiosell_rate_plan_code: "",
  };
}

function defaultForm() {
  return {
    name: "", address: "", neighbourhood: "", city: "", state: "", pincode: "",
    unit_type: "full_unit",
    property_type: "Apartment", listing_type: "short_term",
    bedrooms: "", bathrooms: "", max_guests: "",
    amenities: [], photos: [], videos: [], status: "active",
    nightly_rate: 0, mortgage_amount: 0, emi_amount: "", purchase_price: "",
    min_price: 0, max_price: 0,
    dynamic_pricing_mode: "off",   // "off" | "hostfolio" | "aiosell"
    dp_property_type: "", dp_peak_months: [], dp_peak_days: [],  // Aiosell dynamic pricing profile
    buffer_days: 0, min_turnover_hours: 0, instant_booking: true,
    same_day_cutoff_time: "",
    wifi_name: "", wifi_password: "",
    checkin_time: "14:00", checkout_time: "11:00",
    checkin_instructions: "", house_rules: "", review_link: "",
    event_hosting_enabled: false, event_types: [], event_capacity: 0,
    event_hourly_rate: 0, event_pricing: {}, event_available_hours: "", event_setup_notes: "",
  };
}
